import parseHtml from 'html-react-parser';
import katex from 'katex';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { Icons } from '@/assets/icons/icons';
import { IconButton } from '@/components/ui/IconButton';
import { XStack } from '@/components/ui/layout/layout';
import { classList } from '@/utils/cn';
import { handleCopyText } from '@/utils/platform';

const MAX_CACHE_SIZE = 100;
const latexCache = new Map<string, ReactNode>();

export const getLatexCacheKey = (content: string) => `latex:${content}`;

export const renderBlockLatex = (content: string) => {
  const html = katex.renderToString(content, {
    throwOnError: false,
    displayMode: true,
    strict: false,
    output: 'mathml',
  });
  return parseHtml(html);
};

export const getCachedLatex = (content: string): ReactNode | null => {
  const cacheKey = getLatexCacheKey(content);
  const cachedLatex = latexCache.get(cacheKey);
  if (!cachedLatex) return null;

  latexCache.delete(cacheKey);
  latexCache.set(cacheKey, cachedLatex);
  return cachedLatex;
};

export const setCachedLatex = (content: string, latexNode: ReactNode) => {
  latexCache.set(getLatexCacheKey(content), latexNode);
  if (latexCache.size <= MAX_CACHE_SIZE) return;

  const firstKey = latexCache.keys().next().value;
  if (firstKey) latexCache.delete(firstKey);
};

type LatexPreviewViewProps = {
  latexNode: ReactNode;
  copyText: string;
  onExpand?: () => void;
  interactive?: boolean;
  className?: string;
};

export const LatexPreviewView = ({
  latexNode,
  copyText,
  onExpand,
  interactive = false,
  className,
}: LatexPreviewViewProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    await handleCopyText(copyText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [copyText]);

  return (
    <div
      data-latex-interactive={interactive ? 'true' : 'false'}
      className={classList(
        'h-full w-full',
        'relative',
        'group/latex',
        interactive &&
          'cursor-grab bg-[radial-gradient(theme(colors.gray.4)_1px,transparent_1px)] [background-size:16px_16px]',
        className
      )}
    >
      {onExpand && (
        <div
          className={classList(
            'absolute right-3 top-3 z-10',
            'opacity-0 touch:opacity-100 group-hover/latex:opacity-100 has-[:focus-visible]:opacity-100 group-has-[:focus-visible]/latex:opacity-100',
            'transition-opacity'
          )}
        >
          <IconButton
            size="sm"
            icon={Icons.expand}
            onClick={onExpand}
            tooltip="Expand"
          />
        </div>
      )}
      <XStack
        className={classList(
          'absolute right-3 z-10',
          onExpand ? 'bottom-5' : 'bottom-3',
          'flex gap-2',
          'opacity-0 touch:opacity-100 group-hover/latex:opacity-100 has-[:focus-visible]:opacity-100 group-has-[:focus-visible]/latex:opacity-100',
          'transition-opacity'
        )}
      >
        <IconButton
          size="sm"
          icon={copied ? Icons.check : Icons.copy}
          onClick={handleCopy}
          tooltip={copied ? 'Copied' : 'Copy'}
        />
      </XStack>
      <div className="h-full w-full">
        {interactive ? (
          <TransformWrapper initialScale={1}>
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%' }}
              contentStyle={{ width: '100%', height: '100%' }}
            >
              <LatexContent latexNode={latexNode} />
            </TransformComponent>
          </TransformWrapper>
        ) : (
          <AutoFitLatexContent latexNode={latexNode} />
        )}
      </div>
    </div>
  );
};

const LatexContent = ({ latexNode }: { latexNode: ReactNode }) => (
  <div className="flex h-full w-full items-center justify-center px-6 py-4 text-center text-foreground [&_math]:max-w-full">
    {latexNode}
  </div>
);

const AutoFitLatexContent = ({ latexNode }: { latexNode: ReactNode }) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const viewportElement = viewportRef.current;
    const contentElement = contentRef.current;
    if (!viewportElement || !contentElement) return;

    const updateScale = () => {
      const availableWidth = viewportElement.clientWidth;
      const availableHeight = viewportElement.clientHeight;
      const contentWidth = contentElement.scrollWidth;
      const contentHeight = contentElement.scrollHeight;
      if (
        !availableWidth ||
        !availableHeight ||
        !contentWidth ||
        !contentHeight
      )
        return;

      setScale(
        Math.min(
          1,
          availableWidth / contentWidth,
          availableHeight / contentHeight
        )
      );
    };

    updateScale();
    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(viewportElement);
    resizeObserver.observe(contentElement);
    return () => resizeObserver.disconnect();
  }, [latexNode]);

  return (
    <div
      ref={viewportRef}
      className="flex h-full w-full items-center justify-center overflow-hidden px-6 py-4 text-center text-foreground"
    >
      <div
        ref={contentRef}
        className="origin-center"
        style={{ transform: `scale(${scale})` }}
      >
        {latexNode}
      </div>
    </div>
  );
};
